import React, { useState, useEffect, useRef } from 'react';
import './StudentLiveMonitor.css';
import { buildWebSocketUrl } from './config';

const StudentLiveMonitor = () => {
    const [teachers, setTeachers] = useState([]);
    const [selectedTeacher, setSelectedTeacher] = useState('');
    const [studentName, setStudentName] = useState('');
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [websocket, setWebsocket] = useState(null);
    const [error, setError] = useState('');
    const [isSharing, setIsSharing] = useState(false);
    
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const canvasRef = useRef(null);

    // Load available teachers
    useEffect(() => {
        loadTeachers();
    }, []);

    const loadTeachers = async () => {
        try {
            // Using the backend URL from existing config
            const response = await fetch('/api/teachers');
            const data = await response.json();
            setTeachers(data.teachers || []);
        } catch (error) {
            console.error('Failed to load teachers:', error);
            setError('Failed to load available teachers');
        }
    };

    const connectToTeacher = () => {
        if (!selectedTeacher || !studentName.trim()) {
            setError('Please select a teacher and enter your name');
            return;
        }

        const studentId = `student_${Date.now()}`;
        const wsUrl = `${buildWebSocketUrl()}/ws/student-live/${selectedTeacher}/${studentId}?student_name=${encodeURIComponent(studentName)}`;
        
        const ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
            console.log('✅ Connected to teacher:', selectedTeacher);
            setConnectionStatus('connected');
            setError('');
            setWebsocket(ws);
            
            // Start screen sharing automatically on connection
            startScreenSharing();
        };
        
        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                
                if (data.type === 'heartbeat_ack') {
                    // Keep connection alive
                    console.log('💓 Heartbeat acknowledged');
                }
            } catch (error) {
                console.error('WebSocket message error:', error);
            }
        };
        
        ws.onclose = () => {
            console.log('👋 Disconnected from teacher');
            setConnectionStatus('disconnected');
            setIsSharing(false);
            stopScreenSharing();
            setWebsocket(null);
        };
        
        ws.onerror = (error) => {
            console.error('❌ WebSocket error:', error);
            setError('Failed to connect to teacher. Please try again.');
            setConnectionStatus('error');
        };
    };

    const disconnect = () => {
        if (websocket) {
            websocket.close();
        }
        stopScreenSharing();
    };

    const startScreenSharing = async () => {
        try {
            console.log('📱 Starting screen capture...');
            
            // Request screen capture
            const stream = await navigator.mediaDevices.getDisplayMedia({
                video: {
                    mediaSource: 'screen',
                    width: { ideal: 1920, max: 1920 },
                    height: { ideal: 1080, max: 1080 },
                    frameRate: { ideal: 10, max: 15 }
                },
                audio: false
            });

            streamRef.current = stream;
            
            // Display preview video
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
            
            setIsSharing(true);
            
            // Start capturing frames and sending to teacher
            captureAndSendFrames(stream);
            
            // Handle stream ending
            stream.getVideoTracks()[0].onended = () => {
                console.log('📱 Screen sharing ended by user');
                stopScreenSharing();
            };
            
        } catch (error) {
            console.error('❌ Screen capture failed:', error);
            setError('Screen sharing permission denied or not supported');
        }
    };

    const captureAndSendFrames = (stream) => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        
        if (!video || !canvas || !websocket) return;
        
        const ctx = canvas.getContext('2d');
        
        const captureFrame = () => {
            if (!isSharing || !websocket || websocket.readyState !== WebSocket.OPEN) {
                return;
            }
            
            try {
                // Set canvas size to match video
                canvas.width = video.videoWidth || 1280;
                canvas.height = video.videoHeight || 720;
                
                // Draw video frame to canvas
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                
                // Convert to base64 JPEG (compressed)
                const imageData = canvas.toDataURL('image/jpeg', 0.5);
                
                // Send to teacher via WebSocket
                websocket.send(JSON.stringify({
                    type: 'screen_data',
                    data: imageData,
                    timestamp: new Date().toISOString()
                }));
                
            } catch (error) {
                console.error('Frame capture error:', error);
            }
            
            // Schedule next frame (2 FPS to reduce bandwidth)
            setTimeout(captureFrame, 500);
        };
        
        // Wait for video to be ready
        video.addEventListener('loadeddata', () => {
            captureFrame();
        });
        
        if (video.readyState >= 2) {
            captureFrame();
        }
    };

    const stopScreenSharing = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        
        setIsSharing(false);
    };

    const sendHeartbeat = () => {
        if (websocket && websocket.readyState === WebSocket.OPEN) {
            websocket.send(JSON.stringify({ type: 'heartbeat' }));
        }
    };

    // Send heartbeat every 30 seconds
    useEffect(() => {
        if (connectionStatus === 'connected') {
            const interval = setInterval(sendHeartbeat, 30000);
            return () => clearInterval(interval);
        }
    }, [connectionStatus, websocket]);

    const getStatusColor = () => {
        switch (connectionStatus) {
            case 'connected': return '#10B981';
            case 'error': return '#EF4444';
            default: return '#6B7280';
        }
    };

    const getStatusText = () => {
        switch (connectionStatus) {
            case 'connected': return 'Connected';
            case 'error': return 'Connection Error';
            default: return 'Disconnected';
        }
    };

    return (
        <div className="student-live-monitor">
            <div className="monitor-header">
                <h2>📱 Student Live Monitor</h2>
                <div className="status-indicator">
                    <div 
                        className="status-dot"
                        style={{ backgroundColor: getStatusColor() }}
                    ></div>
                    <span>{getStatusText()}</span>
                </div>
            </div>
            
            {error && (
                <div className="error-message">
                    ⚠️ {error}
                </div>
            )}
            
            {connectionStatus === 'disconnected' && (
                <div className="connection-form">
                    <h3>Connect to Your Teacher</h3>
                    
                    <div className="form-group">
                        <label>Your Name:</label>
                        <input
                            type="text"
                            value={studentName}
                            onChange={(e) => setStudentName(e.target.value)}
                            placeholder="Enter your full name"
                            className="form-input"
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Select Teacher:</label>
                        <select
                            value={selectedTeacher}
                            onChange={(e) => setSelectedTeacher(e.target.value)}
                            className="form-select"
                        >
                            <option value="">Choose a teacher...</option>
                            {teachers.map(teacher => (
                                <option key={teacher.teacher_id} value={teacher.teacher_id}>
                                    {teacher.name} - {teacher.subject}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    <button 
                        onClick={connectToTeacher}
                        disabled={!selectedTeacher || !studentName.trim()}
                        className="connect-button"
                    >
                        🚀 Connect & Start Monitoring
                    </button>
                    
                    <div className="connection-info">
                        <p>ℹ️ This will share your screen with your teacher for monitoring purposes.</p>
                        <p>Your teacher will be able to see what's on your screen in real-time.</p>
                    </div>
                </div>
            )}
            
            {connectionStatus === 'connected' && (
                <div className="connected-view">
                    <div className="connection-details">
                        <h3>Connected to: {teachers.find(t => t.teacher_id === selectedTeacher)?.name}</h3>
                        <p>Student: {studentName}</p>
                        <p>Sharing Status: {isSharing ? '✅ Screen Sharing Active' : '❌ Screen Sharing Inactive'}</p>
                    </div>
                    
                    <div className="sharing-controls">
                        {!isSharing && (
                            <button onClick={startScreenSharing} className="start-sharing-button">
                                📱 Start Screen Sharing
                            </button>
                        )}
                        
                        {isSharing && (
                            <button onClick={stopScreenSharing} className="stop-sharing-button">
                                🛑 Stop Screen Sharing
                            </button>
                        )}
                        
                        <button onClick={disconnect} className="disconnect-button">
                            👋 Disconnect
                        </button>
                    </div>
                    
                    <div className="preview-container">
                        <h4>Your Screen Preview:</h4>
                        <video
                            ref={videoRef}
                            autoPlay
                            muted
                            className="preview-video"
                        />
                        <canvas
                            ref={canvasRef}
                            style={{ display: 'none' }}
                        />
                    </div>
                </div>
            )}
            
            <div className="help-section">
                <h4>Need Help?</h4>
                <ul>
                    <li>Make sure your browser allows screen sharing</li>
                    <li>Select the correct screen/window when prompted</li>
                    <li>Your teacher can see your screen in real-time</li>
                    <li>You can stop sharing or disconnect at any time</li>
                </ul>
            </div>
        </div>
    );
};

export default StudentLiveMonitor;